import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Headphones, ShoppingCart, Smartphone, Star } from "lucide-react";
import { BookCover } from "./BookCover";
import { useCart } from "../context/CartContext";

const BADGES = {
  bestseller: { label: "Bestseller", cls: "bg-amber-400/90 text-amber-950" },
  nowosc: { label: "Nowość", cls: "bg-emerald-400/90 text-emerald-950" },
  promocja: { label: "Promocja", cls: "bg-rose-400/90 text-rose-950" },
  wkrotce: { label: "Wkrótce", cls: "bg-violet-400/90 text-violet-950" },
};

const FORMAT_META = {
  ebook: { icon: BookOpen, label: (p) => `Ebook · ${p.pages || ""} str.` },
  audiobook: { icon: Headphones, label: (p) => `Audiobook · ${p.duration || "MP3"}` },
  aplikacja: { icon: Smartphone, label: (p) => `Aplikacja · ${p.duration || "online"}` },
};

export function ProductCard({ product }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, gx: 50, gy: 50 });
  const { add } = useCart();

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTilt({ rx: (0.5 - py) * 14, ry: (px - 0.5) * 14, gx: px * 100, gy: py * 100 });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0, gx: 50, gy: 50 });

  const badge = BADGES[product.badge];

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`product-card-${product.id}`}
      className={`group relative rounded-2xl glass-panel p-4 transition-[box-shadow,border-color] duration-300 hover:shadow-[0_20px_60px_-10px_rgba(6,182,212,0.35)] hover:border-cyan-400/40 ${product.category === "edukacja-dzieci" ? "kids-card" : ""}`}
      style={{ perspective: "1000px" }}
    >
      {badge && (
        <span className={`absolute left-3 top-3 z-20 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}>
          {badge.label}
        </span>
      )}
      <div
        className="relative transition-transform duration-200 ease-out will-change-transform"
        style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`, transformStyle: "preserve-3d" }}
      >
        <div className="relative" style={{ transform: "translateZ(30px)" }}>
          <Link to={`/produkt/${product.id}`}>
            <div className="transition-transform duration-300 group-hover:scale-[1.05]">
              <BookCover product={product} />
            </div>
          </Link>
          <div
            className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, rgba(255,255,255,0.22), transparent 45%)` }}
          />
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-cyan-400">
          {(() => { const F = FORMAT_META[product.format] || FORMAT_META.ebook; const Icon = F.icon; return (<><Icon className="h-3.5 w-3.5" /><span className="uppercase tracking-wider">{F.label(product)}</span></>); })()}
        </div>
        <Link to={`/produkt/${product.id}`}>
          <h3 className="font-display text-base font-bold leading-tight text-slate-100 transition-colors group-hover:text-cyan-300">
            {product.title}
          </h3>
        </Link>
        <p className="text-xs text-slate-400">{product.author}</p>
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`h-3 w-3 ${i < Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
          ))}
          <span className="ml-1 text-[11px] text-slate-500">({product.reviews})</span>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div>
            <span className="text-lg font-extrabold text-white">{product.price.toFixed(2)} zł</span>
            {product.old_price && (
              <span className="ml-2 text-xs text-slate-500 line-through">{product.old_price.toFixed(2)} zł</span>
            )}
          </div>
          {product.badge !== "wkrotce" ? (
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={() => add(product)}
              className="rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 p-2.5 text-slate-950 shadow-lg shadow-cyan-500/25 transition-transform duration-200 hover:scale-110 active:scale-95"
              aria-label="Dodaj do koszyka"
            >
              <ShoppingCart className="h-4 w-4" />
            </button>
          ) : (
            <span className="text-[11px] font-semibold text-violet-300">Premiera wkrótce</span>
          )}
        </div>
      </div>
    </div>
  );
}
